import React from 'react';

/**
 * DraftBoard - Visual grid showing all draft picks
 * Updates in real-time as picks are made.
 * Minimized by default: only current round. Toggle to expand/collapse.
 */
function DraftBoard({ draft, myTeam, minimized = true, onToggleMinimized }) {
  if (!draft) return null;

  // Validate draft data
  if (!draft.teams || !Array.isArray(draft.teams)) {
    console.error('❌ DraftBoard: Invalid teams data', draft);
    return (
      <div className="card" style={{ padding: '16px' }}>
        <h3>Draft Board</h3>
        <p style={{ color: 'var(--danger)', padding: '20px', textAlign: 'center' }}>
          Unable to display draft board - invalid team data
        </p>
      </div>
    );
  }

  if (!draft.settings || !draft.settings.rounds) {
    console.error('❌ DraftBoard: Invalid settings data', draft);
    return (
      <div className="card" style={{ padding: '16px' }}>
        <h3>Draft Board</h3>
        <p style={{ color: 'var(--danger)', padding: '20px', textAlign: 'center' }}>
          Unable to display draft board - invalid settings
        </p>
      </div>
    );
  }

  const teams = draft.teams || [];
  const totalRounds = draft.settings.rounds;
  const draftOrder = draft.draftOrder || [];
  const picks = draft.picks || [];
  const numTeams = teams.length || 1;
  const currentRoundIndex = Math.floor((draft.currentPickNumber ?? 0) / numTeams);
  const currentRound1Based = currentRoundIndex + 1;

  // Create a map of pick positions to picks
  const pickMap = {};
  picks.forEach(pick => {
    pickMap[pick.pickNumber] = pick;
  });

  // Generate grid data (all rounds)
  const gridData = [];
  for (let round = 0; round < totalRounds; round++) {
    const roundData = [];
    for (let teamIndex = 0; teamIndex < teams.length; teamIndex++) {
      const pickIndex = round * teams.length + teamIndex;
      const pickNumber = pickIndex + 1;
      const pick = pickMap[pickNumber];
      const teamId = draftOrder[pickIndex];
      const team = teams.find(t => t.id === teamId);

      roundData.push({
        pickNumber,
        round: round + 1,
        pickInRound: teamIndex + 1,
        team,
        pick,
        isCurrent: draft.currentPickNumber === pickIndex,
        isMyTeam: team?.id === myTeam?.id
      });
    }
    gridData.push(roundData);
  }

  const roundsToShow = minimized ? [gridData[currentRoundIndex]].filter(Boolean) : gridData;

  return (
    <div className="card" style={{ padding: '16px', overflow: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <h3 style={{ margin: 0 }}>Draft Board{minimized ? ` · Round ${currentRound1Based}` : ''}</h3>
        {typeof onToggleMinimized === 'function' && (
          <button
            type="button"
            onClick={onToggleMinimized}
            className="btn"
            style={{
              padding: '6px 12px',
              fontSize: '13px',
              backgroundColor: 'var(--surface-muted)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer'
            }}
          >
            {minimized ? '▸ Expand' : '▾ Minimize'}
          </button>
        )}
      </div>

      <div className="roster-style-table-wrap">
        <div>
        <table className="roster-style-table" style={{ fontSize: '12px', minWidth: '800px' }}>
          {/* Team Headers */}
          <thead>
            <tr>
              <th style={{
                textAlign: 'center',
                position: 'sticky',
                left: 0,
                zIndex: 2
              }}>
                Rd
              </th>
              {teams.map(team => (
                <th key={team.id} style={{
                  textAlign: 'center',
                  backgroundColor: team.id === myTeam?.id ? 'var(--success-soft)' : undefined,
                  color: team.id === myTeam?.id ? 'var(--success)' : undefined,
                  fontWeight: team.id === myTeam?.id ? 700 : 500
                }}>
                  <div style={{ fontSize: '10px' }}>{team.logoEmoji || '⚾'}</div>
                  <div style={{ fontSize: '11px', marginTop: '4px' }}>
                    {team.name.length > 15 ? team.name.substring(0, 13) + '...' : team.name}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* Draft Grid */}
          <tbody>
            {roundsToShow.map((roundData, idx) => {
              const roundIndex = minimized ? currentRoundIndex : idx;
              return (
              <tr key={roundIndex}>
                {/* Round Number */}
                <td style={{
                  padding: '8px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontFamily: 'var(--font-display)',
                  backgroundColor: 'var(--surface-muted)',
                  borderRight: '2px solid var(--border)',
                  position: 'sticky',
                  left: 0,
                  zIndex: 1
                }}>
                  {roundIndex + 1}
                </td>

                {/* Picks */}
                {roundData.map(cell => {
                  const isEmpty = !cell.pick && !cell.isCurrent;
                  return (
                  <td
                    key={cell.pickNumber}
                    className={`draft-pick-cell${cell.isCurrent ? ' current' : ''}${isEmpty ? ' empty' : ''}`}
                    style={{
                      padding: '6px',
                      textAlign: 'center',
                      backgroundColor: cell.isCurrent
                        ? undefined
                        : cell.isMyTeam && cell.pick
                        ? 'var(--success-soft)'
                        : cell.pick
                        ? 'var(--surface-muted)'
                        : undefined,
                      verticalAlign: 'top',
                      position: 'relative',
                      minHeight: '60px'
                    }}
                  >
                    <div style={{ fontSize: '10px', color: 'var(--muted-foreground)', marginBottom: '4px', fontVariantNumeric: 'tabular-nums' }}>
                      {cell.pickNumber}{cell.isCurrent && !cell.pick ? ' · Now' : ''}
                    </div>

                    {cell.pick ? (
                      <div>
                        <div style={{
                          fontFamily: 'var(--font-display)',
                          fontWeight: 600,
                          fontSize: '11px',
                          marginBottom: '2px',
                          color: cell.isMyTeam ? 'var(--success)' : 'var(--foreground)'
                        }}>
                          {cell.pick.playerName.length > 20
                            ? cell.pick.playerName.substring(0, 18) + '...'
                            : cell.pick.playerName
                          }
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', flexWrap: 'wrap' }}>
                          <span className="pos-tag" style={{ fontSize: '9px', height: '15px', minWidth: '22px', padding: '0 4px' }}>
                            {cell.pick.playerPosition}
                          </span>
                          <span style={{ fontSize: '10px', color: 'var(--muted)' }}>{cell.pick.playerTeam}</span>
                        </div>
                        {cell.pick.pickType === 'auto' && (
                          <div style={{
                            fontSize: '9px',
                            color: 'var(--muted-foreground)',
                            marginTop: '2px',
                            fontStyle: 'italic'
                          }}>
                            Auto
                          </div>
                        )}
                      </div>
                    ) : cell.isCurrent ? (
                      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '11px', color: 'var(--primary)' }}>
                        Drafting…
                      </div>
                    ) : (
                      <div style={{ color: 'var(--muted-foreground)', fontSize: '11px', padding: '10px 0' }}>—</div>
                    )}
                  </td>
                  );
                })}
              </tr>
            );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Legend - hide when minimized to save space */}
      {!minimized && (
      <div style={{
        display: 'flex',
        gap: '16px',
        marginTop: '16px',
        fontSize: '12px',
        justifyContent: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '16px', height: '16px', backgroundColor: 'var(--success-soft)', border: '1px solid var(--success)' }} />
          <span>Your Picks</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '16px', height: '16px', backgroundColor: 'var(--primary-soft)', border: '2px solid var(--primary)' }} />
          <span>Current Pick</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '16px', height: '16px', backgroundColor: 'var(--surface-muted)', border: '1px solid var(--border)' }} />
          <span>Picked</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '16px', height: '16px', backgroundColor: 'var(--surface)', border: '1px dashed var(--border-strong)' }} />
          <span>Upcoming</span>
        </div>
      </div>
      )}
    </div>
  );
}

export default DraftBoard;
